import { useState } from 'react';
import { Plus, AlertTriangle, Check, X } from 'lucide-react';
import { Spinner } from './ui';
import { resolveDexTokenInfo } from '../services/api';
import { saveCustomToken } from '../constants/dexTokens';

/**
 * Inline custom-token resolver — paste any ERC-20 address, we look up
 * symbol/decimals/name via on-chain RPC and add it to the dropdown.
 *
 * Honest about risk: shows "Unverified" badge + warning so users understand
 * custom tokens may not be supported by CoW/1inch resolvers and may not fill.
 */
export default function DexCustomTokenInput({ chainId, onResolved }) {
  const [open, setOpen]       = useState(false);
  const [address, setAddress] = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  const isAddr = /^0x[a-fA-F0-9]{40}$/.test(address.trim());

  async function handleResolve() {
    if (!isAddr || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await resolveDexTokenInfo(chainId, address.trim());
      const tok = {
        symbol:   r.data.symbol,
        address:  r.data.address,
        decimals: r.data.decimals,
        name:     r.data.name,
        custom:   true,
      };
      saveCustomToken(chainId, tok);
      onResolved?.(tok);
      setAddress(''); setOpen(false);
    } catch (err) {
      setError(err?.message || 'Could not resolve token');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full text-xs text-gray-400 hover:text-white flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-dark-700/40 hover:bg-dark-700 border border-dashed border-dark-600 transition-colors">
        <Plus className="w-3 h-3" /> Add custom token
      </button>
    );
  }

  return (
    <div className="bg-dark-700/40 border border-dark-600 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          value={address}
          onChange={e => { setAddress(e.target.value); setError(null); }}
          placeholder="0x… ERC-20 contract address"
          className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-2.5 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        <button onClick={handleResolve} disabled={!isAddr || busy}
          className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-40">
          {busy ? <Spinner size="sm" /> : <Check className="w-3 h-3" />} Add
        </button>
        <button onClick={() => { setOpen(false); setAddress(''); setError(null); }}
          className="p-1.5 rounded-lg text-gray-500 hover:text-white">
          <X className="w-3 h-3" />
        </button>
      </div>
      {error && (
        <p className="text-red-400 text-[11px] flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {error}
        </p>
      )}
      <p className="text-amber-400/80 text-[10px] flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
        Custom tokens are unverified. CoW/1inch resolvers may not have coverage — your order may not fill. Always verify the contract address against the project's official source.
      </p>
    </div>
  );
}
